import { isSameDay } from '@/lib/format';
import type { Message } from '@/types/api';

/**
 * Pure message-list grouping logic, extracted from MessagesList so it can be
 * unit-tested without rendering the virtualizer. Turns an ordered (oldest →
 * newest) message array into the flat row descriptors the virtual list
 * consumes — day separators, the "новое" unread divider, and grouped message
 * rows — with no React or DOM involvement.
 */

/** Flat item descriptor for the virtualizer. */
export type RowItem =
  | { kind: 'day'; key: string; date: Date; unread?: boolean }
  | { kind: 'unread'; key: string }
  | { kind: 'message'; key: string; message: Message; continueGroup: boolean };

/** Consecutive messages from the same author within this gap collapse into a
 *  single visual group (no repeated avatar/name). */
export const GROUP_GAP_MS = 5 * 60_000;

/** Deterministic, stable hash of a string → non-negative int. Used to pick a
 *  per-author name colour from a palette. */
export function nameHash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return Math.abs(h);
}

/**
 * Index (into `ordered`) of the first UNREAD message — where the "новое"
 * divider goes. Returns -1 when there's no unread boundary (no marker, marker
 * not found, or the last read message is the newest one).
 */
export function computeUnreadIndex(ordered: Message[], lastReadId: string | null): number {
  if (!lastReadId) return -1;
  const cutoffIdx = ordered.findIndex((m) => m.id === lastReadId);
  if (cutoffIdx < 0) return -1;
  if (cutoffIdx === ordered.length - 1) return -1;
  return cutoffIdx + 1;
}

/**
 * Build the flat row list. When a day boundary and the unread cutoff land on
 * the same message they merge into ONE divider (date + "НОВОЕ", Discord-style)
 * rather than stacking two. Grouping suppresses the avatar/name on a
 * continuation message (same author, within GROUP_GAP_MS, not across a day or
 * the unread divider).
 */
export function buildMessageRows(ordered: Message[], unreadIdx: number): RowItem[] {
  const out: RowItem[] = [];
  let prev: Message | null = null;
  ordered.forEach((m, idx) => {
    const cur = new Date(m.createdAt);
    const newDay = !prev || !isSameDay(new Date(prev.createdAt), cur);
    const unreadHere = idx === unreadIdx;
    if (newDay) out.push({ kind: 'day', key: `d-${m.id}`, date: cur, unread: unreadHere });
    else if (unreadHere) out.push({ kind: 'unread', key: `u-${m.id}` });
    const continueGroup =
      !newDay &&
      idx !== unreadIdx &&
      !!prev &&
      prev.authorId === m.authorId &&
      cur.getTime() - new Date(prev.createdAt).getTime() < GROUP_GAP_MS;
    out.push({ kind: 'message', key: m.id, message: m, continueGroup });
    prev = m;
  });
  return out;
}
